import React from 'react';
import { useCart } from '../../context/CartContext';
import './index.css';
import bubbleMailerImage from '../../assets/images/bubble_mailer.webp';
import boxImage from '../../assets/images/packaging_box_preview.webp';

const Shipping: React.FC = () => {
  const { cart } = useCart();

  // Convert cart items to display format for shipping information
  const products = cart.map((item, index) => ({
    id: `shipping-${index + 1}`,
    name: item.name,
    weight: `${item.weight_grams}g`,
    dimensions: getDimensionsForWeight(item.weight_grams),
    quantity: item.quantity,
    description: getDescriptionForWeight(item.weight_grams)
  }));

  // Helper function to get dimensions based on weight
  function getDimensionsForWeight(weightGrams: number): string {
    if (weightGrams <= 10) {
      return '2" x 2.75" x 0.78"';
    } else if (weightGrams <= 15) {
      return '33" length, 0.25" diameter beads';
    } else if (weightGrams <= 20) {
      return '3.25" x 3.5" x 0.2"';
    } else {
      return '4" x 3" x 0.5"';
    }
  }

  // Helper function to get description based on weight
  function getDescriptionForWeight(weightGrams: number): string {
    if (weightGrams <= 10) {
      return 'Compact and lightweight charm perfect for keychains, bags, or small displays.';
    } else if (weightGrams <= 15) {
      return 'Elegant beaded necklace with consistent sizing for a professional look.';
    } else if (weightGrams <= 20) {
      return 'Lightweight sports-themed items perfect for team merchandise and collectibles.';
    } else {
      return 'Durable item designed for long-lasting use and display.';
    }
  }

  return (
    <div className="shipping-container">
      <h1>Shipping Information</h1>
      <div className="shipping-intro">
        <p>All our products are carefully packaged and shipped with care. <br/>Smaller items are packaged in 7" x 4" bubble mailers, while larger items are packaged in durable packaging boxes.</p>
      </div>
      
      <div className="shipping-content">
        <div className="shipping-left-column">
          <div className="products-list">
            {products.length > 0 ? (
              <table className="product-table">
                <thead>
                  <tr>
                    <th className="header-name">Product</th>
                    <th className="header-weight">Weight</th>
                    <th className="header-size">Size</th>
                    <th className="header-quantity">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="product-row">
                      <td className="product-name">{product.name}</td>
                      <td className="product-weight">{product.weight}</td>
                      <td className="product-dimensions">{product.dimensions}</td>
                      <td className="product-quantity">{product.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-cart-message">
                <h3>No items in cart</h3>
                <p>Add items to your cart to see their shipping specifications here.</p>
                <a href="/shop" className="shop-link">Browse Products</a>
              </div>
            )}
          </div>
        </div>
        
        <div className="shipping-right-column">
          <div className="bubble-mailer-section">
            <h2 className="bubble-mailer-title">Packaging Details</h2>
            <div className="bubble-mailer-card">
              <img 
                src={bubbleMailerImage} 
                alt="7 inch by 4 inch bubble mailer" 
                className="bubble-mailer-image"
              />
              <div className="bubble-mailer-info">
                <h3>Smaller Bubble Mailer</h3>
                <p>Small items are packaged in 7" x 4" bubble mailers to ensure safe delivery. These protective envelopes feature:</p>
                <ul>
                  <li>Bubble wrap interior for cushioning</li>
                  <li>Water-resistant exterior</li>
                  <li>Self-sealing adhesive strip</li>
                  <li>Perfect size for our product range</li>
                  <li>Eco-friendly materials</li>
                </ul>
              </div>
            </div>
            <div className="box-card">
              <img 
                src={boxImage} 
                alt="Packaging box" 
                className="box-image"
              />
            </div>
            <div className="box-info">
              <h3>Packaging Box</h3>
              <p>Larger orders are packaged in durable packaging boxes to ensure safe delivery. These protective boxes feature:</p>
              <ul>
                <li>Bubble wrap interior for cushioning</li>
                <li>Water-resistant exterior</li>
                <li>Self-sealing adhesive strip</li>
                <li>Perfect size for our product range</li>
                <li>Eco-friendly materials</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <div className="shipping-notes">
        <h2>Shipping Notes</h2>
        <ul>
          <li>All items are packaged individually in protective materials</li>
          <li>Standard shipping typically takes 3-5 business days</li>
          <li>Express shipping options available at checkout</li>
          <li>International shipping available for select countries</li>
          <li>Tracking information provided for all shipments</li>
        </ul>
      </div>
    </div>
  );
};

export default Shipping;
